import { useEffect, useState, useRef, Dispatch, SetStateAction } from 'react';
import { IoPersonAdd } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { IModal } from 'customTypes';
import { BtnBlue, BtnGray } from '../Button';
import { ModalBackground, Modal } from '../../assets/styles/Modal';
import { isMobile } from '../../utils/checkMobile';
import { downloadFile } from '../../utils/flutterBridge';
import FormatImg from '../FormatImg';
import styled from 'styled-components';

const ThumbnailRoot = styled.div`
  min-width: inherit;
  width: 50rem !important;
  height: 80dvh !important;
  max-height: 80dvh;

  .inputForm-head {
    .modalHeaderTitle {
      min-width: 0;
      flex: 1;

      .file-title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        min-width: 0;

        span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
        }
      }
    }
  }

  .inputForm-body {
    overflow-y: hidden;
    justify-content: center;
    align-items: center;

    .modal-contents {
      justify-content: center;
      align-items: center;

      .thumbnailModal {
        height: 100%;
      }
    }
  }

  @media screen and (max-width: 768px) {
    & {
      position: absolute;
      width: 100dvw !important;
      height: 100dvh !important;
      // height: calc(var(--vh, 1vh) * 100) !important;
      top: 0;
      padding-bottom: env(safe-area-inset-bottom);
      min-height: inherit;
      max-height: inherit;
    }
  }
`;

const Thumbnail = ({ openModal, setOpenModal, download = false }: any) => {
  const { t } = useTranslation();
  const [data, setData] = useState(openModal.data);
  const imgRef = useRef<HTMLImageElement>(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const lastTouchDistanceRef = useRef(0);
  const lastTouchCenterRef = useRef({ x: 0, y: 0 });
  const lastScaleRef = useRef(1);

  useEffect(() => {
    setData(openModal.data);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setImageError(false);
    setImageLoading(true);
    lastScaleRef.current = 1;
  }, [openModal.data]);

  // 이미지가 이미 로드되어 있는지 확인 (캐시된 이미지의 경우 onLoad가 발생하지 않을 수 있음)
  useEffect(() => {
    if (!data?.aImgPath || !imgRef.current) {
      return undefined;
    }

    const img = imgRef.current;

    // 이미지가 이미 완전히 로드된 경우
    if (img.complete && img.naturalHeight > 0) {
      setImageLoading(false);
      setImageError(false);
      return undefined;
    }

    // 이미지 로드 실패를 감지하기 위한 타임아웃
    const timeout = setTimeout(() => {
      if (imageLoading && !img.complete) {
        console.warn('이미지 로드 타임아웃:', data.aImgPath);
        setImageError(true);
        setImageLoading(false);
      }
    }, 10000); // 10초 후 타임아웃

    return () => clearTimeout(timeout);
  }, [data?.aImgPath, imageLoading]);

  // passive: false로 이벤트 리스너 등록 (아이폰에서 preventDefault 작동하도록)
  useEffect(() => {
    if (!isMobile() || !imgRef.current || imageLoading || imageError) {
      return undefined;
    }

    const img = imgRef.current;

    // 이미지가 완전히 로드되었는지 확인
    if (!img.complete || img.naturalHeight === 0) {
      return undefined;
    }

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const center = {
          x: (touch1.clientX + touch2.clientX) / 2,
          y: (touch1.clientY + touch2.clientY) / 2,
        };
        lastTouchDistanceRef.current = distance;
        lastTouchCenterRef.current = center;
        lastScaleRef.current = scale;
      } else if (e.touches.length === 1) {
        setIsDragging(true);
        dragStartRef.current = {
          x: e.touches[0].clientX - position.x,
          y: e.touches[0].clientY - position.y,
        };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault(); // passive: false이므로 작동함

      if (e.touches.length === 2) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const center = {
          x: (touch1.clientX + touch2.clientX) / 2,
          y: (touch1.clientY + touch2.clientY) / 2,
        };

        if (lastTouchDistanceRef.current > 0) {
          const scaleChange = distance / lastTouchDistanceRef.current;
          const newScale = Math.max(1, Math.min(lastScaleRef.current * scaleChange, 5));

          if (img) {
            const rect = img.getBoundingClientRect();
            const imgCenterX = rect.left + rect.width / 2;
            const imgCenterY = rect.top + rect.height / 2;

            const scaleDiff = newScale / lastScaleRef.current;
            let newX = position.x + (center.x - lastTouchCenterRef.current.x) + (center.x - imgCenterX) * (scaleDiff - 1);
            let newY = position.y + (center.y - lastTouchCenterRef.current.y) + (center.y - imgCenterY) * (scaleDiff - 1);

            // 경계 제한 계산
            const container = img.parentElement;
            if (container && newScale > 1) {
              const containerRect = container.getBoundingClientRect();
              const scaledWidth = (rect.width / lastScaleRef.current) * newScale;
              const scaledHeight = (rect.height / lastScaleRef.current) * newScale;

              const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
              const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);

              newX = Math.max(-maxX, Math.min(maxX, newX));
              newY = Math.max(-maxY, Math.min(maxY, newY));
            }

            setScale(newScale);
            setPosition({ x: newX, y: newY });
            lastScaleRef.current = newScale;
          }
        }

        lastTouchDistanceRef.current = distance;
        lastTouchCenterRef.current = center;
      } else if (e.touches.length === 1 && isDragging) {
        let newX = e.touches[0].clientX - dragStartRef.current.x;
        let newY = e.touches[0].clientY - dragStartRef.current.y;

        // 경계 제한 계산 (단일 터치 드래그)
        if (img && scale > 1) {
          const container = img.parentElement;
          if (container) {
            const containerRect = container.getBoundingClientRect();
            const imgRect = img.getBoundingClientRect();

            // 스케일된 이미지의 실제 크기
            const scaledWidth = imgRect.width;
            const scaledHeight = imgRect.height;

            // 이미지가 컨테이너보다 클 때만 경계 제한 적용
            if (scaledWidth > containerRect.width) {
              const maxX = (scaledWidth - containerRect.width) / 2;
              newX = Math.max(-maxX, Math.min(maxX, newX));
            } else {
              newX = 0; // 이미지가 컨테이너보다 작으면 중앙 정렬
            }

            if (scaledHeight > containerRect.height) {
              const maxY = (scaledHeight - containerRect.height) / 2;
              newY = Math.max(-maxY, Math.min(maxY, newY));
            } else {
              newY = 0; // 이미지가 컨테이너보다 작으면 중앙 정렬
            }
          }
        } else if (scale === 1) {
          // 스케일이 1이면 중앙 정렬
          newX = 0;
          newY = 0;
        }

        setPosition({ x: newX, y: newY });
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      // 핀치 줌 종료 시 lastScaleRef는 이미 handleTouchMove에서 업데이트됨
      lastTouchDistanceRef.current = 0;
    };

    const handleDoubleClick = () => {
      if (scale === 1) {
        setScale(2);
        lastScaleRef.current = 2;
      } else {
        setScale(1);
        setPosition({ x: 0, y: 0 });
        lastScaleRef.current = 1;
      }
    };

    // passive: false로 등록하여 preventDefault가 작동하도록 함
    img.addEventListener('touchstart', handleTouchStart, { passive: false });
    img.addEventListener('touchmove', handleTouchMove, { passive: false });
    img.addEventListener('touchend', handleTouchEnd, { passive: false });
    img.addEventListener('touchcancel', handleTouchEnd, { passive: false });
    img.addEventListener('dblclick', handleDoubleClick);

    return () => {
      img.removeEventListener('touchstart', handleTouchStart);
      img.removeEventListener('touchmove', handleTouchMove);
      img.removeEventListener('touchend', handleTouchEnd);
      img.removeEventListener('touchcancel', handleTouchEnd);
      img.removeEventListener('dblclick', handleDoubleClick);
    };
  }, [scale, position, isDragging, isMobile, imageLoading, imageError]);

  // 닫기버튼 클릭
  const onClickClose = () => {
    setOpenModal((prev: IModal) => ({ ...prev, status: false }));
    setScale(1);
    setPosition({ x: 0, y: 0 });
    lastScaleRef.current = 1;
  };

  const handleFileDownload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!data?.aImgPath) return;

    try {
      await downloadFile(data.aImgPath, data.aImgPath.split('/').pop() || 'thumbnail.jpg');
    } catch (error) {
      console.error('handleFileDownload error:', error);
    }
  };

  return (
    <ModalBackground onClick={onClickClose}>
      <Modal maxWidth='80vw' role='presentation' onClick={(e) => e.stopPropagation()} style={{ height: 'inherit' }}>
        <ThumbnailRoot className='modal'>
          <div className='inputForm-head'>
            <div className='modalHeaderTitle'>
              {data?.fileName && (
                <div className='file-title'>
                  <FormatImg format={data?.fileFormat} type='text' textSize='sm' />
                  <span>{data?.fileName}</span>
                </div>
              )}
            </div>
            <div className='closeBtn' onClick={onClickClose} role='presentation'>
              <span className='material-symbols-rounded'>close</span>
            </div>
          </div>
          <div className='inputForm-body'>
            <div className='modal-contents col-w100'>
              <div className='thumbnailWrapper thumbnailModal'>
                {data?.aImgPath ? (
                  <>
                    {imageLoading && !imageError && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%', minHeight: '200px', position: 'absolute', top: 0, left: 0 }}>
                        <div style={{ color: 'var(--text-secondary)' }}>{t('이미지 로딩 중...')}</div>
                      </div>
                    )}
                    {!imageError && (
                      <img
                        ref={imgRef}
                        className='thumbnail'
                        src={data.aImgPath}
                        alt='thumbnail'
                        style={{
                          ...data.style,
                          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                          transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                          opacity: imageLoading ? 0 : 1,
                          objectFit: 'contain',
                          width: '100%',
                          height: 'auto',
                        }}
                        draggable={!isMobile()}
                        onLoad={(e) => {
                          // console.log('이미지 로드 성공:', data.aImgPath);
                          setImageLoading(false);
                          setImageError(false);
                        }}
                        onError={(e) => {
                          console.error('이미지 로드 실패:', {
                            originalUrl: data.aImgPath,
                            normalizedUrl: data.aImgPath,
                            error: e,
                          });
                          setImageError(true);
                          setImageLoading(false);
                        }}
                        onLoadStart={() => {
                          // console.log('이미지 로드 시작:', data.aImgPath);
                          setImageLoading(true);
                          setImageError(false);
                        }}
                      />
                    )}
                  </>
                ) : (
                  <IoPersonAdd size={50} />
                )}
              </div>
              {!openModal.thumbnailOnly && (
                <dl className='thumbnailData' style={{ width: '100%' }}>
                  <div className='flex-basic'>
                    <dt className='text_primary'>{t('출역일자')}</dt>
                    <dd className='text_tertiary'>{data?.aDate}</dd>
                  </div>
                  <div className='flex-basic'>
                    <dt className='text_primary'>{t('출역시간')}</dt>
                    <dd className='text_tertiary'>{data?.aTime}</dd>
                  </div>
                  <div className='flex-basic'>
                    <dt className='text_primary'>{t('장비명')}</dt>
                    <dd className='text_tertiary'>{data?.fName}</dd>
                  </div>
                  <div className='flex-basic'>
                    <dt className='text_primary'>{t('출입위치')}</dt>
                    <dd className='text_tertiary'>{data?.fLocationName}</dd>
                  </div>
                </dl>
              )}
            </div>
          </div>
          <div className='modal-footer flex-end'>
            {download && data?.aImgPath && !imageError && <BtnBlue onClick={(e) => handleFileDownload(e)}>{t('다운로드')}</BtnBlue>}
            <BtnGray onClick={onClickClose}>{t('닫기')}</BtnGray>
          </div>
        </ThumbnailRoot>
      </Modal>
    </ModalBackground>
  );
};

export default Thumbnail;
